import { useEffect, useState } from "react";

import { TopBar } from "@/components/shell/TopBar";
import { dayMonthMaybeYear } from "@/lib/dates";
import { isTextEntry } from "@/lib/dom";
import { createGroup, GroupNameTaken, type CardGroup } from "@/lib/groups";
import { announceGroupsChanged } from "@/components/groups/useGroups";

/**
 * 混同's index (日本-601, frame A): every group, a checkbox to pick it for a drill, and the row to
 * open it.
 *
 * **Picking and opening are two acts, and two targets.** Browsing a group must never change what a
 * drill will include, so the checkbox is its own control and the rest of the row opens. On a phone
 * both are 44px.
 *
 * The picked set is component state, deliberately not in `NavState`: it is churn on the way to the
 * drill, and the drill's URL is what records which groups were chosen.
 */
export function GroupIndex({
  groups,
  loaded,
  error,
  isPhone,
  footer,
  onOpen,
  onDrill,
}: {
  groups: readonly CardGroup[];
  loaded: boolean;
  error: string | null;
  isPhone: boolean;
  footer?: React.ReactNode;
  onOpen: (groupId: string) => void;
  onDrill: (groupIds: string[]) => void;
}) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [name, setName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  // A group deleted elsewhere must not stay picked, or the drill would ask for it and refuse.
  const alive = groups.filter((g) => picked.has(g.id));
  const pickedIds = alive.map((g) => g.id);
  const pickedCards = new Set(alive.flatMap((g) => g.noteIds)).size;
  const totalCards = new Set(groups.flatMap((g) => g.noteIds)).size;

  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createGroup(trimmed);
      setName("");
      setCreateError(null);
      announceGroupsChanged();
    } catch (e) {
      setCreateError(
        e instanceof GroupNameTaken || e instanceof Error ? e.message : String(e),
      );
    }
  };

  // `D` starts the drill — the one key this screen has, advertised on the button it duplicates.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTextEntry(event.target)) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if ((event.key === "d" || event.key === "D") && pickedIds.length > 0 && pickedCards > 0) {
        event.preventDefault();
        onDrill(pickedIds);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDrill, pickedCards, pickedIds]);

  const newGroup = (
    <form
      className={isPhone ? "flex gap-2 px-4 py-3" : "flex gap-2 py-3"}
      onSubmit={(event) => {
        event.preventDefault();
        void create();
      }}
    >
      <input
        id="groups-new-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="新しいグループ名"
        aria-label="新しいグループ名"
        data-check="groups.new-name"
        className="h-9 min-w-0 flex-1 rounded-[var(--r-2)] border border-line-strong bg-void px-[10px] text-[14px] text-ink outline-none placeholder:text-ink-4 focus:border-accent"
      />
      <button
        type="submit"
        disabled={!name.trim()}
        data-check="groups.create"
        className="rounded-[var(--r-2)] border border-line-strong px-3 text-secondary font-semibold text-ink-2 disabled:opacity-40"
      >
        作成
      </button>
    </form>
  );

  const list =
    groups.length === 0 ? (
      loaded ? (
        <div
          className="flex flex-col items-center gap-2 px-6 py-12 text-center"
          data-check="groups.empty"
        >
          <span className="text-body font-semibold text-ink-2">グループはまだありません</span>
          <span className="max-w-[46ch] text-secondary text-ink-4">
            復習中や単語一覧で、カードの ⋯ メニューから「グループに追加」を選ぶと作成できます。
          </span>
        </div>
      ) : (
        <div className="px-4 py-6 text-secondary text-ink-3">…</div>
      )
    ) : (
      <ul
        className={
          isPhone ? "" : "rounded-[var(--r-3)] border border-line bg-panel"
        }
        data-check="groups.list"
      >
        {!isPhone && (
          <li className="flex justify-between px-4 pb-2 pt-3 text-meta text-ink-4">
            <span>グループ</span>
            <span>チェックしたものをドリル</span>
          </li>
        )}
        {groups.map((group) => {
          const on = picked.has(group.id);
          return (
            <li
              key={group.id}
              data-check="groups.row"
              className="flex items-center border-b border-line-soft last:border-b-0"
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-label={`${group.name}をドリルに含める`}
                data-check="groups.pick"
                onClick={() => toggle(group.id)}
                className="flex h-11 w-12 flex-shrink-0 items-center justify-center"
              >
                <span
                  className={[
                    "flex items-center justify-center rounded-[var(--r-2)] border text-[11px] font-bold",
                    isPhone ? "h-[22px] w-[22px]" : "h-4 w-4",
                    on ? "border-accent bg-accent text-white" : "border-line-strong",
                  ].join(" ")}
                >
                  {on ? "✓" : ""}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onOpen(group.id)}
                data-check="groups.open"
                className="flex min-h-11 min-w-0 flex-1 items-center gap-3 py-2.5 pr-4 text-left hover:bg-[color:var(--tab-hover)]"
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-jp text-[15px] font-medium text-ink-2">
                    {group.name}
                  </span>
                  <span className="text-meta text-ink-4">
                    {dayMonthMaybeYear(new Date(group.createdAt * 1000))}
                  </span>
                </span>
                <span className="font-mono text-meta text-ink-3">{group.noteCount}枚</span>
                <span aria-hidden className="text-ink-4">
                  ›
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );

  const drillBar = (
    <div
      className={[
        "flex flex-shrink-0 items-center gap-3.5 border-t border-line",
        isPhone ? "bg-panel px-4 py-3" : "pt-3.5",
      ].join(" ")}
    >
      <span className="min-w-0 flex-1 text-secondary text-ink-3" data-check="groups.summary">
        {pickedIds.length > 0 ? (
          <>
            <b className="font-medium text-ink">{pickedIds.length}グループ</b> ·{" "}
            <b className="font-medium text-ink">{pickedCards}枚</b> · シャッフル
          </>
        ) : (
          "未選択"
        )}
      </span>
      <button
        type="button"
        disabled={pickedIds.length === 0 || pickedCards === 0}
        onClick={() => onDrill(pickedIds)}
        data-check="groups.drill"
        className="rounded-[var(--r-2)] bg-accent px-[13px] py-2 text-secondary font-semibold text-white disabled:bg-raised disabled:text-ink-off"
      >
        ドリル開始
        {!isPhone && <span className="ml-2 font-mono text-[12px] opacity-70">D</span>}
      </button>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <TopBar title="混同" destinationId="groups">
        <span className="text-meta text-ink-4">
          {groups.length}グループ · {totalCards}枚
        </span>
      </TopBar>
      {error && (
        <p className="shrink-0 border-b border-accent-line bg-accent-tint px-[18px] py-2 text-secondary text-accent-hi">
          {error}
        </p>
      )}
      {isPhone ? (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {list}
            {newGroup}
            {createError && <p className="px-4 text-meta text-accent-hi">{createError}</p>}
          </div>
          {groups.length > 0 && drillBar}
          {footer}
        </>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex min-h-full w-full max-w-[760px] flex-col px-6 py-5">
            {list}
            {newGroup}
            {createError && <p className="pb-2 text-meta text-accent-hi">{createError}</p>}
            <div className="flex-1" />
            {groups.length > 0 && drillBar}
          </div>
        </div>
      )}
    </div>
  );
}
